import { useLayoutEffect, useState } from 'react'

/**
 * @returns Whether the document is the focused element.
 */
export function useDocumentFocus() {
  const [isFocused, setIsFocused] = useState(
    typeof document !== 'undefined' && document.hasFocus(),
  )

  // useLayoutEffect must be used instead of useEffect. Since useEffect fires
  // after layout and paint, there is a chance a user could quickly change to a
  // newly created tab and the focus event would be missed before this hook had
  // a chance to run, resulting in a stale isFocused value.
  useLayoutEffect(() => {
    const onFocus = () => setIsFocused(true)
    const onBlur = () => setIsFocused(false)
    const onVisibilityChange = () =>
      setIsFocused(document.visibilityState === 'visible')

    // Desktop browsers support focus/blur events well, those event handlers must
    // be in place for this feature to work on desktop. But mobile browsers do
    // not, and instead work well with visibilitychange. Changing the focus state
    // on both focus/blur & visibilitychange makes this work on both desktop &
    // mobile.
    window.addEventListener('focus', onFocus)
    window.addEventListener('blur', onBlur)
    document.addEventListener('visibilitychange', onVisibilityChange)

    return () => {
      window.removeEventListener('focus', onFocus)
      window.removeEventListener('blur', onBlur)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [])

  return isFocused
}
